import { Box, Button, CircularProgress, Dialog, DialogActions, Divider, Stack, Typography } from "@mui/material";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { inputTypes, selectOptionsKeyName, statusOptions } from "data/globalData";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	sourcingOptionCogsColumn,
	fabricCodeSearchColumn,
	finalMRPColumn,
	hsnCodeColumn,
	fwMaterialCodeSearchColumn,
	washCareColumn,
} from "./data";
import { fabricCategoryColumn, fabricFinalColumns } from "../../FabricMaster/data";
import axios from "axios";
import { PimDashboardState } from "context/PimDashboardContext";
import RenderAttributeInputs from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderAttributes/RenderAttributeInputs";
import { attrGrpAttrAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";
import {
	attrConditionalDisplayColObj,
	attrGroupLinkedTables,
	formatColumnValue,
	getColDefaultValue,
	publishTableColumn,
	styleColorAssocObj,
} from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { TableImgComponent } from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/ColorsSection/ColorsSection";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";
import { footwearMaterialColumns, upperMaterialColumn } from "../../FootwearMaterialMaster/data";
import RenderConditionalAttributeInputs from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderConditionalAttributes/RenderConditionalAttributeInputs";

const EditFabricStyleDetails = ({ modal, setModal, selectedStyle, setSelectedStyle, saveCallback }) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen, Loadingopen } = GlobalState();
	const { allAttributes, attrGroupsWithAttr } = PimDashboardState();

	const [fullStyleData, setFullStyleData] = useState();
	const [updatedFabriDetails, setUpdatedFabricDetails] = useState({});
	const [searchObj, setSearchObj] = useState();
	const [loading, setLoading] = useState(true);
	const [colors, setColors] = useState(true);

	const [colorTableColumns, setColorTableColumns] = useState([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [rejectingColorId, setRejectingColorId] = useState(null);
	const [colorToReject, setColorToReject] = useState(null);

	const publishedColorCount = useMemo(
		() => (Array.isArray(colors) ? colors.filter((row) => row?.status === statusOptions.PUBLISHED.key).length : 0),
		[colors],
	);

	const canRejectColor = (row) =>
		row?.status === statusOptions.PUBLISHED.key && row?.status !== statusOptions.DESIGNER_PENDING.key && publishedColorCount > 1;

	const fabricDisabledColumns = useMemo(() => {
		if (isEditMode) return [];
		return allAttributes
			.filter(
				(row) =>
					row.group_id === "fabric_details" ||
					row.key === hsnCodeColumn.key ||
					row.key === finalMRPColumn.key ||
					row.key === washCareColumn.key ||
					row.key === "sizes",
			)
			.map((row) => row.key);
	}, [allAttributes, isEditMode]);

	const toReadOnlyColumns = (columns) =>
		isEditMode ? columns : columns.map((col) => ({ ...col, input_type: inputTypes.AUTO_GENERATE.key }));

	const noopSetData = useCallback(() => {}, []);

	const handleModalClose = () => {
		setIsEditMode(false);
		setModal(false);
		setSelectedStyle();
	};

	const handleCancelEdit = async () => {
		setIsEditMode(false);
		setLoading(true);
		await fetchStyleDetails();
	};

	const handleRejectColor = async (colorRow) => {
		if (!canRejectColor(colorRow)) return;

		try {
			setRejectingColorId(colorRow.id);
			setLoadingOpen(true);
			await axios.put("/api/sourcing/reject_color_by_sourcing", {
				style_id: selectedStyle?.style_id,
				color_id: colorRow.id,
			});
			setColors((prev) => prev.map((row) => (row.id === colorRow.id ? { ...row, status: statusOptions.REJECTED.key } : row)));
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "success", message: "Color rejected successfully" });
			if (typeof saveCallback === "function") saveCallback(selectedStyle);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setRejectingColorId(null);
			setColorToReject(null);
			setLoadingOpen(false);
		}
	};

	const handleRejectClick = (colorRow) => {
		if (!canRejectColor(colorRow)) return;
		setColorToReject(colorRow);
	};

	const fetchFabricMasterdataFromSearch = async (offset = 0, limit = 20, searchTerm = "") => {
		const query = {
			limit,
			offset,
		};
		if (searchTerm) query["search"] = searchTerm;
		// construct your request logic
		const res = await axios.get(`/api/designer/get_fabric_data_by_search`, {
			params: query,
			query: {},
		});

		return res;
	};

	const fetchFootwearMaterialFromSearch = async (offset = 0, limit = 20, searchTerm = "") => {
		const query = {
			limit,
			offset,
		};
		if (searchTerm) query["search"] = searchTerm;
		const res = await axios.get(`/api/designer/get_footwear_material_data_by_search`, {
			params: query,
			query: {},
		});

		return res;
	};

	const handleFabricCodeSelect = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(searchObj);

		if (!dataObj || !dataObj[fabricCodeSearchColumn.key]) {
			setSearchObj(dataObj);
			return;
		}
		let updatedFabricValues = [fabricCategoryColumn, ...Object.values(fabricFinalColumns)].reduce((acc, curr) => {
			acc[curr.key] = dataObj[fabricCodeSearchColumn.key][curr.key];
			return acc;
		}, {});
		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};

	const handleFootwearMaterialSelect = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(searchObj);
		if (!dataObj || !dataObj[fwMaterialCodeSearchColumn.key]) {
			setSearchObj(dataObj);
			return;
		}
		let updatedFabricValues = [upperMaterialColumn, ...Object.values(footwearMaterialColumns)].reduce((acc, curr) => {
			acc[curr.key] = dataObj[fwMaterialCodeSearchColumn.key][curr.key];
			return acc;
		}, {});
		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};
	const handleFabricInputChange = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(fullStyleData);

		let updatedFabricValues = allAttributes
			.filter(
				(item) =>
					item.group_id === "fabric_details" &&
					!fabricFinalColumns[item.key] &&
					item.key !== fabricCategoryColumn.key &&
					!footwearMaterialColumns[item.key],
			)
			.reduce((acc, curr) => {
				acc[curr.key] = dataObj[attrGroupLinkedTables.style_details.key][curr.key];
				return acc;
			}, {});

		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};
	const handleFabricSaveClick = async (publish = false) => {
		if (publish) {
			const isFootwear = fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "FOOT WEAR";

			if (isFootwear) {
				// Check footwear material columns
				const missingCols = Object.values(footwearMaterialColumns).filter(
					(col) => !fullStyleData[attrGroupLinkedTables.style_details.key]?.[col.key],
				);
				if (missingCols.length) {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: `Please Fill the values for ${missingCols.map((row) => row.name).join(", ")}.`,
					});
					return;
				}
			} else {
				// Check fabric columns
				if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.fabric_category) {
					setSnackBarOpen(true);
					setSnackBar({ ...snackBar, severity: "error", message: "Please Select Fabric Code First" });
					return;
				}

				const missingCols = [...Object.values(fabricFinalColumns)].filter(
					(col) => !fullStyleData[attrGroupLinkedTables.style_details.key]?.[col.key],
				);
				if (missingCols.length) {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: `Please Fill the values for ${missingCols.map((row) => row.name).join(", ")}.`,
					});
					return;
				}
			}

			if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.[washCareColumn.key]) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: "Please Add Wash Care Instructions" });
				return;
			}

			if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.[hsnCodeColumn.key]) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: "Please Add HSN Code" });
				return;
			}
			if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.[washCareColumn.key]) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: "Please Add Wash Care Instructions" });
				return;
			}

			// colors pending
			let colorsPending = [];
			colors?.forEach((row) => {
				if (row?.status !== statusOptions.REJECTED.key)
					if (!+row[sourcingOptionCogsColumn.key] > 0)
						colorsPending.push({ name: `${row.color}-${row.pallete}`, key: `${row.color}-${row.pallete}` });
			});
			if (colorsPending.length) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: `Please Fill the ${sourcingOptionCogsColumn.name} for ${colorsPending.map((row) => row.name).join(",")}`,
				});
				return;
			}
		}

		const body = {
			...updatedFabriDetails,
			style_id: selectedStyle?.style_id,
			id: fullStyleData[attrGroupLinkedTables.style_details.key]?.id,
			[styleColorAssocObj]: colors
				.filter((row) => {
					if (publish) return true;
					return row.is_updated === true;
				})
				.map((row) => ({
					id: row?.id,
					[sourcingOptionCogsColumn.key]: row[sourcingOptionCogsColumn.key],
				})),
		};
		try {
			setLoadingOpen(true);
			await axios.put(`/api/designer/update_style_fabric${publish ? "?publish=true" : ""}`, body).then((res) => {
				saveCallback({
					...selectedStyle,
					...body,
					status: publish ? statusOptions.PUBLISHED.key : selectedStyle.status,
				});
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "success", message: "Data updated Successfully" });
				handleModalClose();
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
		}
	};

	const fetchStyleDetails = async () => {
		try {
			const res = await axios.get(`/api/designer/style_by_id/${selectedStyle?.style_id}`);
			setFullStyleData(res.data);
			setUpdatedFabricDetails(
				allAttributes
					.filter((attr) => attr.group_id === "fabric_details")
					.reduce((acc, row) => {
						acc[row.key] = res.data?.[attrGroupLinkedTables.style_details.key]?.[row.key];
						return acc;
					}, {}),
			);
			setColors(res.data?.[attrGroupLinkedTables.color.key]);
			setColorTableColumns(
				allAttributes
					.filter((row) => row.linked_table === attrGroupLinkedTables.color.key)
					.map((row) => {
						if (row.key === sourcingOptionCogsColumn.key)
							return {
								...row,
								...sourcingOptionCogsColumn,
							};
						else return row;
					}),
			);
			// setSearchObj({[fabricCodeSearchColumn.key] :fabricCodeSearchColumn?.[].getOptionLabel})
			setLoading(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
		}
	};

	const handleColorCogsChange = (newRow, oldRow) => {
		if (!isNaN(newRow[sourcingOptionCogsColumn.key]) || newRow[sourcingOptionCogsColumn.key] > 0) {
			setColors((prev) =>
				prev.map((item) =>
					item.id === newRow.id
						? { ...item, [sourcingOptionCogsColumn.key]: newRow[sourcingOptionCogsColumn.key], is_updated: true }
						: item,
				),
			);
			return newRow;
		}
	};

	useEffect(() => {
		if (modal) setIsEditMode(false);
	}, [modal, selectedStyle?.style_id]);

	useEffect(() => {
		if (selectedStyle) fetchStyleDetails();
	}, [modal]);

	return (
		<Dialog fullWidth maxWidth="xl" open={modal} onClose={() => handleModalClose()} scroll="body" keepMounted={false}>
			{loading ? (
				<Box width="100%" height={300} display="flex" justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" />
				</Box>
			) : (
				<>
					<Box display="flex" width="100%" justifyContent="space-around" columnGap={2}>
						<Stack
							width="65%"
							flex={1}
							p={2}
							boxSizing="border-box"
							spacing={1}
							// alignItems="center"
							margin="auto"
							// justifyContent="space-around"
						>
							<Box
								display="flex"
								width="100%"
								justifyContent="space-between"
								alignItems="center"
								flexWrap="wrap"
								gap={1}>
								<Typography variant="h6">{isEditMode ? "Edit Fabric Details" : "Fabric Style Details"}</Typography>
								<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
									{isEditMode ? (
										<>
											<Button variant="outlined" size="small" onClick={() => handleCancelEdit()}>
												Cancel
											</Button>
											<Button variant="contained" size="small" onClick={() => handleFabricSaveClick(false)}>
												Save
											</Button>
											{fullStyleData?.[attrGroupLinkedTables.style.key]?.status ===
											statusOptions.PUBLISHED.key ? (
												<Button
													variant="contained"
													color="success"
													size="small"
													disableFocusRipple
													disableRipple
													disableElevation>
													Published
												</Button>
											) : (
												<Button
													variant="contained"
													color="success"
													size="small"
													onClick={() => handleFabricSaveClick(true)}
													disabled={
														fullStyleData?.[attrGroupLinkedTables.style.key]?.status !==
														statusOptions.SOURCING_PENDING.key
													}>
													Save And Publish
												</Button>
											)}
										</>
									) : (
										<>
											<Button variant="outlined" size="small" onClick={() => handleModalClose()}>
												Close
											</Button>
											<Button variant="contained" size="small" onClick={() => setIsEditMode(true)}>
												Edit
											</Button>
										</>
									)}
								</Box>
							</Box>

							{fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "FOOT WEAR" ? (
								<SearchInputFieldWithDebounce
									column={fwMaterialCodeSearchColumn}
									data={searchObj}
									setData={handleFootwearMaterialSelect}
									fetchData={fetchFootwearMaterialFromSearch}
									selectOptKeyName={selectOptionsKeyName}
									disableLabel
									storeValueAs="object"
									width="300px"
									disabled={!isEditMode}
								/>
							) : (
								<SearchInputFieldWithDebounce
									column={fabricCodeSearchColumn}
									data={searchObj}
									setData={handleFabricCodeSelect}
									fetchData={fetchFabricMasterdataFromSearch}
									selectOptKeyName={selectOptionsKeyName}
									disableLabel
									storeValueAs="object"
									width="300px"
									disabled={!isEditMode}
								/>
							)}
							{/* fabric Details */}
							<Box sx={{ width: "100%" }}>
								{Object.keys(attrGroupsWithAttr)
									.reduce((acc, row) => {
										if (attrGroupsWithAttr[row]?.group_id === "fabric_details") acc.push(attrGroupsWithAttr[row]);
										return acc;
									}, [])
									?.map((row) => {
										return (
											<Box width="100%">
												{row?.data?.[attrConditionalDisplayColObj.key] ? (
													<RenderConditionalAttributeInputs
														columns={toReadOnlyColumns([
															...row[attrGrpAttrAssocObj]?.filter(
																(row) =>
																	row.key !== finalMRPColumn.key &&
																	row.key !== hsnCodeColumn.key &&
																	row.key !== washCareColumn.key &&
																	row.key !== "sizes",
															),
														])}
														setData={isEditMode ? handleFabricInputChange : noopSetData}
														data={fullStyleData}
														attrGrp={row}
														itemWidth="32%"
														width={"100%"}
														setErrorObj={() => {}}
														errorObj={{}}
													/>
												) : (
													<RenderAttributeInputs
														columns={[
															...row[attrGrpAttrAssocObj]?.filter(
																(row) =>
																	row.key !== finalMRPColumn.key &&
																	row.key !== hsnCodeColumn.key &&
																	row.key !== washCareColumn.key &&
																	row.key !== "sizes",
															),
														]}
														setData={isEditMode ? handleFabricInputChange : noopSetData}
														data={fullStyleData}
														attrGrp={row}
														itemWidth="32%"
														width={"100%"}
														setErrorObj={() => {}}
														errObj={{}}
														disabledColumns={fabricDisabledColumns}
													/>
												)}
											</Box>
										);
									})}
							</Box>

							<Box sx={{ width: "100%" }}>
								<RenderAttributeInputs
									columns={toReadOnlyColumns([
										...(allAttributes.some((row) => row?.key === washCareColumn.key)
											? allAttributes.reduce((acc, row) => {
													if (row?.key === washCareColumn.key) acc.push({ ...row, ...washCareColumn });
													return acc;
												}, [])
											: [washCareColumn]),
										...allAttributes.reduce((acc, row) => {
											if (row?.key === "sizes") acc.push({ ...row, input_type: inputTypes.AUTO_GENERATE.key });
											return acc;
										}, []),
									])}
									setData={isEditMode ? handleFabricInputChange : noopSetData}
									data={fullStyleData}
									attrGrp={attrGroupsWithAttr["fabric_details"]}
									itemWidth="32%"
									width={"100%"}
									setErrorObj={() => {}}
									errObj={{}}
									disabledColumns={fabricDisabledColumns}
								/>
							</Box>

							{/* color section */}
							<Box width="100%">
								<Typography variant="h6">COGS</Typography>
							</Box>
							{/* MRP */}
							<Box sx={{ width: "100%" }}>
								{/* <Typography variant="h6">{row[attrGrpTableStrColumnsObj.name.key]}</Typography> */}
								<RenderAttributeInputs
									columns={toReadOnlyColumns([
										...allAttributes.reduce((acc, row) => {
											if (row?.key === hsnCodeColumn.key) acc.push({ ...row, ...hsnCodeColumn });
											if (row?.key === finalMRPColumn.key) acc.push({ ...row, ...finalMRPColumn });
											return acc;
										}, []),
									])}
									setData={isEditMode ? handleFabricInputChange : noopSetData}
									data={fullStyleData}
									attrGrp={attrGroupsWithAttr["fabric_details"]}
									itemWidth="32%"
									width={"100%"}
									setErrorObj={() => {}}
									errObj={{}}
									disabledColumns={fabricDisabledColumns}
								/>
							</Box>
							{colorTableColumns?.length >= 0 && (
								<Box sx={{ height: 290, width: "100%", minHeight: 0 }}>
									<DataGrid
										rows={colors ? colors : []}
										columns={[
											{
												headerName: "Image",
												field: "img",
												minWidth: 120,
												headerAlign: "center",
												flex: 1,
												renderCell: ({ row }) => {
													return <TableImgComponent data={row} column="url" />;
												},
											},
											...colorTableColumns?.map((colorCol) => {
												return {
													field: colorCol?.key,
													headerName: colorCol?.name?.toUpperCase(),
													minWidth: 100,
													headerAlign: "center",
													align: "center",
													flex: 1,
													editable: isEditMode && colorCol?.key === sourcingOptionCogsColumn.key,
													...colorCol,
												};
											}),
											publishTableColumn(null, true),
											...(!isEditMode
												? [
														{
															field: "action",
															headerName: "Action",
															minWidth: 110,
															headerAlign: "center",
															align: "center",
															flex: 1,
															sortable: false,
															filterable: false,
															renderCell: ({ row }) => (
																<Button
																	size="small"
																	color="error"
																	variant="outlined"
																	disabled={!canRejectColor(row) || rejectingColorId === row.id}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleRejectClick(row);
																	}}>
																	Reject
																</Button>
															),
														},
													]
												: []),
										]}
										sx={{
											height: 290,
											"& .MuiDataGrid-main": { overflow: "auto" },
											"& .MuiDataGrid-virtualScroller": { overflow: "auto !important" },
											"& .MuiDataGrid-row": {
												borderBottom: "1px solid lightgray",
												cursor: "pointer",
											},
											"& .MuiDataGrid-columnHeader": {
												cursor: "pointer",
												// background: grey[300],
												borderRight: "1px solid lightgray",
											},
										}}
										columnHeaderHeight={35}
										rowHeight={60}
										disableRowSelectionOnClick
										pageSizeOptions={[5, 10]}
										hideFooter
										pagination={false}
										disableAutosize
										disableVirtualization
										processRowUpdate={isEditMode ? handleColorCogsChange : undefined}
									/>
								</Box>
							)}
						</Stack>
						<Divider flexItem orientation="vertical" />
						<Stack width="30%" maxWidth={"320px"}>
							<Typography variant="h6" sx={{ width: "100%", mb: 1 }}>
								Style Details
							</Typography>
							{allAttributes
								.filter((row) => row.linked_table === attrGroupLinkedTables.style.key)
								.map((row) => {
									return (
										<Box
											width={"90%"}
											key={row?.name}
											display="flex"
											border={`1px solid white`}
											boxSizing="border-box">
											<Typography
												variant="subtitle2"
												color={grey[200]}
												sx={{
													width: "38%",
													p: "4px 2%",
													background: "#000",
													overflow: "hidden",
													borderRadius: "1px 0px 0px 1px",
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="subtitle2"
												sx={{
													width: "55%",
													p: "4px 2%",
													background: grey[200],
													fontWeight: 400,
													overflow: "hidden",
													borderRadius: " 0px 1px 1px 0px",
												}}>
												{fullStyleData?.[row.linked_table][row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
							<Typography variant="h6">Suggestions</Typography>
							{allAttributes
								.filter((row) => row.group_id === "suggestions")
								.map((row) => {
									return (
										<Box
											width={"90%"}
											key={row?.name}
											display="flex"
											border={`1px solid white`}
											boxSizing="border-box">
											<Typography
												variant="subtitle2"
												color={grey[200]}
												sx={{
													width: "38%",
													p: "3px 2%",
													background: "#000",
													overflow: "hidden",
													borderRadius: "1px 0px 0px 1px",
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="subtitle2"
												sx={{
													width: "55%",
													p: "3px 2%",
													background: grey[200],
													fontWeight: 400,
													overflow: "hidden",
													borderRadius: " 0px 1px 1px 0px",
												}}>
												{fullStyleData?.[row.linked_table][row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
						</Stack>
					</Box>

					<Dialog fullWidth maxWidth="xs" open={Boolean(colorToReject)} onClose={() => setColorToReject(null)}>
						<Box p={2}>
							<Typography variant="h6" gutterBottom>
								Reject color?
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Are you sure you want to reject{" "}
								<strong>
									{colorToReject?.color}
									{colorToReject?.pallete ? ` - ${colorToReject.pallete}` : ""}
								</strong>
								? At least one published color must remain on this style.
							</Typography>
						</Box>
						<DialogActions sx={{ px: 2, pb: 2 }}>
							<Button variant="outlined" onClick={() => setColorToReject(null)}>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="error"
								disabled={rejectingColorId === colorToReject?.id}
								onClick={() => colorToReject && handleRejectColor(colorToReject)}>
								Confirm Reject
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</Dialog>
	);
};

export default EditFabricStyleDetails;
